import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';
import Icon from '../../../components/AppIcon';

const VarianceAnalysisChart = ({ data }) => {
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(value);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-card border border-border rounded-lg p-4 card-shadow">
          <p className="text-sm font-medium text-foreground mb-2">{`${label} 2025`}</p>
          {payload?.map((entry, index) => (
            <div key={index} className="flex items-center space-x-2 text-sm">
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: entry?.color }}
              />
              <span className="text-muted-foreground">{entry?.dataKey}:</span>
              <span className="font-medium text-foreground">{formatCurrency(entry?.value)}</span>
            </div>
          ))}
          {payload?.[0] && (
            <div className="mt-2 pt-2 border-t border-border">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Variance:</span>
                <span className={`font-medium ${
                  payload?.[0]?.payload?.variance >= 0 ? 'text-success' : 'text-error'
                }`}>
                  {formatCurrency(payload?.[0]?.payload?.variance)}
                </span>
              </div>
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 card-shadow">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <Icon name="TrendingUp" size={20} className="text-primary" />
          <div>
            <h3 className="text-lg font-semibold text-foreground">Budget vs Actual Variance Analysis</h3>
            <p className="text-sm text-muted-foreground">Monthly comparison of budgeted vs actual spending</p>
          </div>
        </div>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-primary rounded-full" />
            <span className="text-xs text-muted-foreground">Budgeted</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-success rounded-full" />
            <span className="text-xs text-muted-foreground">Actual</span>
          </div>
        </div>
      </div>

      <div className="h-80 mb-6">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={data}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
            <XAxis 
              dataKey="month" 
              tick={{ fontSize: 12 }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis 
              tick={{ fontSize: 12 }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(value) => `$${(value / 1000)}K`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Line
              type="monotone"
              dataKey="budgeted"
              stroke="hsl(var(--primary))"
              strokeWidth={3}
              dot={{ fill: "hsl(var(--primary))", strokeWidth: 2, r: 4 }}
              name="Budgeted"
            />
            <Line
              type="monotone"
              dataKey="actual"
              stroke="hsl(var(--success))"
              strokeWidth={3}
              dot={{ fill: "hsl(var(--success))", strokeWidth: 2, r: 4 }}
              name="Actual"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Variance Bar Chart */}
      <div className="border-t border-border pt-6">
        <div className="mb-4">
          <h4 className="text-md font-semibold text-foreground mb-1">Monthly Variance</h4>
          <p className="text-sm text-muted-foreground">Positive values indicate overspending, negative values indicate savings</p>
        </div>
        
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis 
                dataKey="month" 
                tick={{ fontSize: 12 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis 
                tick={{ fontSize: 12 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(value) => `$${(value / 1000)}K`}
              />
              <Tooltip 
                formatter={(value) => [formatCurrency(value), 'Variance']}
                labelFormatter={(label) => `${label} 2025`}
                contentStyle={{
                  backgroundColor: 'hsl(var(--card))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px'
                }}
              />
              <Bar
                dataKey="variance"
                fill={(entry) => entry >= 0 ? 'hsl(var(--error))' : 'hsl(var(--success))'}
                radius={[2, 2, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6 pt-6 border-t border-border">
        <div className="text-center">
          <div className="text-2xl font-bold text-foreground">
            {formatCurrency(data?.reduce((sum, item) => sum + Math.abs(item?.variance), 0) / data?.length)}
          </div>
          <div className="text-sm text-muted-foreground">Avg. Monthly Variance</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-success">
            {data?.filter(item => item?.variance < 0)?.length}
          </div>
          <div className="text-sm text-muted-foreground">Months Under Budget</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-error">
            {data?.filter(item => item?.variance > 0)?.length}
          </div>
          <div className="text-sm text-muted-foreground">Months Over Budget</div>
        </div>
      </div>
    </div>
  );
};

export default VarianceAnalysisChart;